import { useQuery } from '@tanstack/react-query';
import { subscriptionService } from '../../../services/subscription';
import type { PaymentMethod } from '../../../models/subscription';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';

interface UsePaymentMethodsOptions {
  organizationId?: string;
  enabled?: boolean;
}

/**
 * Hook to get payment methods for an organization
 * @param options.organizationId Optional - if provided, fetches for that specific org (requires OWNER role)
 *                               If not provided, uses the current organization from context
 * @param options.enabled Optional - if false, the query will not run (useful for SUPER_ADMIN who doesn't have access)
 */
export const usePaymentMethods = (
  options?: UsePaymentMethodsOptions | string,
) => {
  // Support both old signature (string) and new signature (object)
  const explicitOrganizationId =
    typeof options === 'string' ? options : options?.organizationId;
  const enabled =
    typeof options === 'object' ? options?.enabled !== false : true;

  const currentOrganizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );

  /**
   * The cache key must name an organization even when the caller passes none.
   *
   * Callers like the billing page invoke this with no argument and let the request be
   * scoped by the `X-Organization-ID` header. That left every organization sharing the
   * key `['paymentMethods', undefined]`, so after switching organizations the list showed
   * the *previous* organization's cards for the whole 5-minute staleTime — and deleting
   * one sent that organization's paymentMethodId with the new organization's header,
   * which the backend correctly refused with "You can only delete payment methods for
   * your own organization". Same reasoning as useSubscription, which already does this.
   */
  const organizationId = explicitOrganizationId ?? currentOrganizationId;

  return useQuery<PaymentMethod[], Error>({
    queryKey: ['paymentMethods', organizationId],
    // Only forward an explicitly requested organization: passing the current one would
    // turn every call into the SUPER_ADMIN cross-org form, which needs OWNER on that org.
    queryFn: () =>
      subscriptionService.getPaymentMethods(explicitOrganizationId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: enabled && !!organizationId,
  });
};
